import React, { useState } from 'react';
import { useModal } from '@gumtree/shell/src/use-modal';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import { Icon, Button } from '@gumtree/ui-library';
import { toast } from '@gumtree/ui-library/src/toast';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@gumtree/ui-library/src/base/theme';
import { SellerSkillItem } from '@gumtree/shared/src/model/listing.model';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { UpdateRequestQuoteFormData } from '@gumtree/shared/src/types/request-quote-form';
import { revealPhoneUtil } from './phoneUtil';

interface RevealedNumber {
    data?: string;
    expired?: boolean;
    valid?: boolean;
}

interface PhoneContactButtonProps {
    replyPhone: any;
    isUserLoggedIn: any;
    isCurrentUserSeller: any;
    revealSellerTelephoneNumberToken: any;
    userLoggedIn: any;
    showRevealNumber: any;
    isLoginRequiredToContactForVipCategory: any;
    revealNumberUnmasked: any;
    revealPhoneCallbackUrl: any;
    id: any;
    isFromMotorsFeed: any;
    domain: any;
    style: any;
    buttonStyle: any;
    trackEventPayload: any;

    adSource?: string;
    imageUrl?: string;
    title?: string;
    thirdPartySellerId?: string;
    allSellerSkills?: SellerSkillItem[];
    updateRequestQuoteFormData?: UpdateRequestQuoteFormData;
    requestId?: string;
    categoryId?: number;
}

const PhoneContactButton = ({
    replyPhone, 
    isUserLoggedIn,

    // 以下来自 sellerContactDetails
    isCurrentUserSeller,
    revealSellerTelephoneNumberToken,
    userLoggedIn,
    showRevealNumber,
    isLoginRequiredToContactForVipCategory,
    revealNumberUnmasked,
    revealPhoneCallbackUrl,

    // 以下来自 adDetails
    id, 
    isFromMotorsFeed,

    // 以下来自 baseConfig
    domain,

    style,
    buttonStyle,

    trackEventPayload,
    
    adSource,
    imageUrl,
    title,
    thirdPartySellerId,
    allSellerSkills,
    updateRequestQuoteFormData,
    requestId,
    categoryId,
}: PhoneContactButtonProps) => {
    const [revealedNumber, setRevealedNumber] = useState<RevealedNumber>({})
    const { openDialog } = useModal();
    const isMobile = useMediaQuery({ maxWidth: breakpoints.medium });

    const haveRevealedNumber = !!(
        revealedNumber?.data &&
        !revealedNumber?.expired &&
        revealedNumber?.valid
    );

    function revealPhone(e: React.MouseEvent, redirectToApp?: string) {
        trackButtonClick();
        
        e.preventDefault();
        e.stopPropagation();

        if (adSource === 'Bark' && updateRequestQuoteFormData) {
            if (!isUserLoggedIn) {
                openDialog(e, LoginModalType.REQUEST_QUOTE, true);
            } else {
                updateRequestQuoteFormData.setProfile(imageUrl || '');
                updateRequestQuoteFormData.setTitle(title || '');
                updateRequestQuoteFormData.setSellerId(thirdPartySellerId || '');
                updateRequestQuoteFormData.setAdvertId(id || '');
                updateRequestQuoteFormData.setSellerSkills(allSellerSkills || []);
                updateRequestQuoteFormData.setPreferredContactType('call');
                updateRequestQuoteFormData.setAdCategoryId(categoryId || 0);
                updateRequestQuoteFormData.openModal();
            }
            return;
        }

        if (isUserLoggedIn) {
            handleRevealPhone(e as React.MouseEvent<HTMLButtonElement>, redirectToApp);
        } else {
            const url = new URL(location.href);
            url.searchParams.set('revealPhone', 'true');
            history.replaceState({}, '', url);
            openDialog(e, LoginModalType.REVEAL_PHONE, true);
        }
    }

    const handleRevealPhone = async (event: React.MouseEvent, redirectToApp) => {
        // 确保 revealPhoneNumber 的参数与新组件的 props 和 state 一致
        const data = await revealPhoneUtil({
            e: event,

            // 以下来自 sellerContactDetails
            revealSellerTelephoneNumberToken,
            userLoggedIn,
            showRevealNumber,
            isLoginRequiredToContactForVipCategory,

            // 以下来自 adDetails
            id, 
            isFromMotorsFeed,

            // 以下来自 baseConfig
            domain,

            redirectToApp,

            trackEventPayload,
        });

        setRevealedNumber(data)
    };


    const revealPhoneM = (type) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleRevealPhone(e, type);
    };

    const handleCopyPhoneNumber = async (e: React.MouseEvent) => {
        trackButtonClick();

        e.preventDefault();
        e.stopPropagation();
        if (navigator.clipboard && revealedNumber?.data) {
            try {
                await navigator.clipboard.writeText(revealedNumber.data);
                toast.success('Phone number copied to clipboard!');
            } catch (err) {
                console.error('Failed to copy text: ', err);
                toast.error('Failed to copy phone number.');
            }
        }
    };


    let revealNumberUrl, phoneNumber;

    if (haveRevealedNumber) {
        revealNumberUrl = '#'; // 当号码已显示时，点击不再跳转，而是复制
        phoneNumber = revealedNumber.data;
    } else if (isLoginRequiredToContactForVipCategory && !showRevealNumber) {
        revealNumberUrl = revealPhoneCallbackUrl;
        phoneNumber = replyPhone;
    } else if (showRevealNumber) {
        revealNumberUrl = '#';
        phoneNumber = revealNumberUnmasked;
    } else {
        revealNumberUrl = '#';
        phoneNumber = replyPhone;
    }

    const trackButtonClick = () => {
        trackGA4Event<any>({
            event: 'ad_click_button_info',
            requestId,
            listingId: id
        });
    }
    
    const phoneLabel = adSource !== 'Bark' ? 'Show phone number' : 'Request a call back';

    if (adSource !== 'Bark' && !replyPhone) {
        return null; // 如果没有电话号码，不渲染按钮
    }

    if (adSource !== 'Bark' && isMobile) {
        return (
            <Button
                style={style}
                fullWidth
                noFollow
                disabled={Boolean(isCurrentUserSeller)}
                label='Call'
                icon={ <Icon type="phoneCall" /> }
                data-q='reply-phone'
                onClick={revealPhoneM('tel')}
                display="secondary"
            />
        )

    } else {
        return (
            // 当 haveRevealedNumber 为 true 时，href 不再重要，因为点击事件被接管
            // 但为了保持 a 标签的语义，可以保留 href='#'
            <a style={style} href={revealNumberUrl} onClick={haveRevealedNumber ? handleCopyPhoneNumber : revealPhone}>
                <Button
                    style={buttonStyle}
                    display="secondary"
                    label={
                        haveRevealedNumber
                            ? phoneNumber // 直接显示号码
                            : phoneLabel
                    }
                    // disabled={showNewPhoneLabel ? false : isDisabled}
                    icon={<Icon type="phoneCall" />}
                    secondIcon={ haveRevealedNumber ? <Icon type="copy" data-testid="icon-copy" /> : null}
                    data-q='reply-panel-reveal-btn'
                    fullWidth
                />
            </a>
        );

    }
};

export default PhoneContactButton;
